/**
 * 把 "我在哪个房间" 的真相 (lobby.me.room_id) 映射到路由位置。
 *
 * snapshot is truth: 无论是创建/加入/被踢/房间被删, 后端最终都会通过
 * `lobby_state` 或 `player_status_changed` 把 me.room_id 调到正确状态;
 * 前端只需要监听这一个值, 业务代码不再各自 router.push。
 *
 * 两道防线:
 *
 * 1. 路由守卫 (beforeEach) —— 手动 URL 跳转 / 浏览器后退 等"路由变了但 room_id 没变"的场景。
 *    比如用户在 /room/r1 时手动把地址栏改成 /home, 守卫把他踢回 /room/r1。
 * 2. lobby.me.room_id watcher —— 服务端推送驱动的场景 (join / leave / kick / room_removed)。
 *    room_id 改变时主动 push 到正确路由。
 *
 * 二者共用同一个 targetRoute() 推导函数, 保证决策一致。
 *
 * 关键约定:
 * - `lobby.ready` 为 false 时认为 me.room_id 尚未确定, 不踢人 (避免初始化窗口闪烁)。
 * - 进房 / 换房用 push, 出房 (=>lobby) 用 replace, 避免后退栈污染。
 */

import { watch } from 'vue'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { useRouter } from 'vue-router'

import { useLobbyStore } from '@/stores/lobby'

let installed = false

export function installRouteSync(): void {
  if (installed) return
  installed = true

  const router = useRouter()
  const lobby  = useLobbyStore()

  function targetFor(to: RouteLocationNormalized): RouteLocationRaw | null {
    if (to.name === 'login') return null
    const myRoom = lobby.me?.room_id ?? null

    if (myRoom) {
      // 已确认在某个房间 —— 必须停在那个房间的路由上。
      if (to.name !== 'room' || to.params.roomId !== myRoom) {
        return { name: 'room', params: { roomId: myRoom } }
      }
      return null
    }

    // 尚未收到 lobby_state 时不踢, 避免初始加载窗口 (REST 已通过但 WS 还在握手) 把人弹回 lobby。
    if (lobby.ready && to.name === 'room') {
      return { name: 'lobby' }
    }
    return null
  }

  // ── 1) 路由守卫
  router.beforeEach((to) => {
    const next = targetFor(to)
    return next ?? true
  })

  // ── 2) lobby state 变更驱动
  watch(
    () => [lobby.me?.room_id ?? null, lobby.ready] as const,
    () => {
      const current = router.currentRoute.value
      const next = targetFor(current)
      if (!next) return
      // 出房用 replace, 进房 / 换房用 push
      const goingToLobby = typeof next === 'object'
        && 'name' in next && next.name === 'lobby'
      if (goingToLobby) router.replace(next)
      else              router.push(next)
    },
    { immediate: true },
  )
}
